import AuthService from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export interface SystemSettings {
	title: string;
	logoUrl: string;
	loginWelcomeTitle: string;
	loginWelcomeText: string;
	loginFooterText: string;
	/** Whether the login page offers Microsoft sign-in. */
	ssoEnabled: boolean;
	/** Colour instance id from theme-instances.json. Empty = the JSON's default. */
	themeInstance?: string;
	slUrl: string;
	apiGatewayUrl: string;
	slCompanyDb: string;
	slUsername: string;
	slPassword: string;
	slPasswordSet: boolean;
}

export interface EmailConfig {
	configured: boolean;
	companyId?: number;
	provider: string;
	authMode: "application" | "delegated";
	tenantId: string;
	clientId: string;
	clientSecretSet: boolean;
	mailboxConnected: boolean;
	connectedMailboxEmail: string;
	connectedMailboxName: string;
	senderEmail: string;
	senderName: string;
	isActive: boolean;
}

export interface EmailConfigUpdate {
	provider?: string;
	authMode?: "application" | "delegated";
	tenantId: string;
	clientId: string;
	clientSecret?: string;
	senderEmail?: string;
	senderName?: string;
	isActive?: boolean;
}

export interface SlConnectionStatus {
	connected: boolean;
}

export interface BrandingInfo {
	title: string;
	logoUrl: string;
	/** Login-page overrides. Empty means "use the i18n bundle default". */
	loginWelcomeTitle: string;
	loginWelcomeText: string;
	loginFooterText: string;
	/** Whether to offer the Microsoft sign-in button. Public, so the login page can read it. */
	ssoEnabled: boolean;
	/** Colour instance id from theme-instances.json. Empty = the JSON's default. */
	themeInstance?: string;
}

const SettingsService = {
	async getBranding(): Promise<BrandingInfo> {
		const response = await fetch("/api/branding");
		if (!response.ok) {
			throw new Error("Failed to fetch branding");
		}
		return response.json() as Promise<BrandingInfo>;
	},

	async getSettings(): Promise<SystemSettings> {
		const response = await AuthService.authenticatedFetch("/api/settings");
		if (!response.ok) {
			throw new Error("Failed to fetch settings");
		}
		return response.json() as Promise<SystemSettings>;
	},

	async updateSettings(
		settings: Partial<SystemSettings>,
	): Promise<SystemSettings> {
		const response = await AuthService.authenticatedFetch("/api/settings", {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(settings),
		});
		if (!response.ok) {
			throw new Error("Failed to update settings");
		}
		return response.json() as Promise<SystemSettings>;
	},

	async getSlStatus(): Promise<SlConnectionStatus> {
		const response = await AuthService.authenticatedFetch("/api/sl/status");
		if (!response.ok) {
			throw new Error("Failed to fetch SL status");
		}
		return response.json() as Promise<SlConnectionStatus>;
	},

	async connectSl(): Promise<SlConnectionStatus> {
		const response = await AuthService.authenticatedFetch("/api/sl/connect", {
			method: "POST",
		});
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Connection failed" }))) as {
				message: string;
			};
			throw new Error(error.message);
		}
		return response.json() as Promise<SlConnectionStatus>;
	},

	async getEmailConfig(): Promise<EmailConfig> {
		const response = await AuthService.authenticatedFetch(
			"/api/settings/email-config",
		);
		if (!response.ok) {
			throw new Error("Failed to fetch email config");
		}
		return response.json() as Promise<EmailConfig>;
	},

	async updateEmailConfig(config: EmailConfigUpdate): Promise<EmailConfig> {
		const response = await AuthService.authenticatedFetch(
			"/api/settings/email-config",
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(config),
			},
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Failed to update email config" }))) as {
				message: string;
			};
			throw new Error(error.message);
		}
		return response.json() as Promise<EmailConfig>;
	},

	async getEmailMailboxConnectUrl(): Promise<{ authorizationUrl: string }> {
		const response = await AuthService.authenticatedFetch(
			"/api/settings/email-config/connect",
			{
				method: "POST",
			},
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Failed to start mailbox connect" }))) as {
				message: string;
			};
			throw new Error(error.message);
		}
		return response.json() as Promise<{ authorizationUrl: string }>;
	},

	/**
	 * Application mode's answer to the delegated "Connect mailbox" step: checks
	 * the saved credentials, the granted Mail.Send permission and the sender
	 * mailbox without sending anything. A failed check still resolves — the
	 * level carries the outcome.
	 */
	async verifyEmailConfig(): Promise<{
		level: "success" | "warning" | "error";
		message: string;
		mailbox?: string;
	}> {
		const response = await AuthService.authenticatedFetch(
			"/api/settings/email-config/verify",
			{ method: "POST", headers: { "Content-Type": "application/json" } },
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Verification failed" }))) as {
				message: string;
			};
			throw new Error(error.message);
		}
		return response.json() as Promise<{
			level: "success" | "warning" | "error";
			message: string;
			mailbox?: string;
		}>;
	},

	async testEmailConfig(
		email: string,
	): Promise<{ message: string; sentTo: string }> {
		const response = await AuthService.authenticatedFetch(
			"/api/settings/email-config/test",
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email }),
			},
		);
		if (!response.ok) {
			const error = (await response
				.json()
				.catch(() => ({ message: "Test failed" }))) as {
				message: string;
				error?: string;
			};
			throw new Error(error.error || error.message);
		}
		return response.json() as Promise<{ message: string; sentTo: string }>;
	},
};

export default SettingsService;
